import React, { FC, ReactNode, Suspense } from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router';
import ErrorBoundary from 'src/components/ErrorBoundary';
import NewRenterExperienceGuard from 'src/guards/NewRenterExperienceGuard';
import { AIFileUploadV2ContextProvider } from 'src/contexts/AIFileUploadV2Context';
import OnBehalfOfBanner from 'src/components/NewRenterInterface/shared/OnBehalfOfBanner';
import { useOnBehalfOf } from 'src/hooks/useOnBehalfOf';
import AIFileUploadV2RouteSkeleton from './AIFileUploadV2RouteSkeleton';

interface AIFileUploadV2LayoutProps {
  children: ReactNode;
  certificationId?: number;
  userId?: number;
  /** When false, the layout does not render the OnBehalfOfBanner — the child
   *  page is responsible for rendering it in the correct position (e.g. after
   *  its own page header). Defaults to true. */
  showBannerInLayout?: boolean;
}

const AIFileUploadV2Layout: FC<AIFileUploadV2LayoutProps> = ({
  children,
  certificationId,
  userId,
  showBannerInLayout = true,
}) => {
  const theme = useTheme();
  const { state: locationState } = useLocation();

  // For cert-scoped uploads, userId comes from the route prop.
  // For profile-survey uploads (Data Vault, no certificationId), userId is
  // passed via router state by WorkItemRenderer when in proxy mode.
  const effectiveUserId =
    userId ??
    (locationState as { subjectUserId?: number } | null)?.subjectUserId;

  const { subjectName: onBehalfOfName } = useOnBehalfOf({
    subjectUserId: effectiveUserId ?? null,
    certId: certificationId ?? null,
  });

  return (
    <NewRenterExperienceGuard>
      <ErrorBoundary>
        <AIFileUploadV2ContextProvider
          certificationId={certificationId}
          userId={effectiveUserId}
        >
          <Box
            sx={{
              minHeight: '100%',
              height: '100%',
              backgroundColor: theme.palette.background.neutral,
            }}
          >
            {showBannerInLayout && <OnBehalfOfBanner name={onBehalfOfName} />}
            <Suspense fallback={<AIFileUploadV2RouteSkeleton />}>
              {children}
            </Suspense>
          </Box>
        </AIFileUploadV2ContextProvider>
      </ErrorBoundary>
    </NewRenterExperienceGuard>
  );
};

export default AIFileUploadV2Layout;
